import React, { useState } from "react";
import "./UploadFile.css";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import LoadingButton from "@mui/lab/LoadingButton";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useCSVReader } from "react-papaparse";
import axios from "axios";
import Typography from "@mui/material/Typography";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const UploadFile = ({ refresh, setRefresh, setLoading }) => {
  const { CSVReader } = useCSVReader();

  const [Data, setData] = useState(null);
  const [MessageText, setMessageText] = useState(null);
  const [failedEmails, setFailedEmails] = useState([]);
  const [LoadingBtn, setLoadingBtn] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false); // only true after API call
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  function manipulateData(data) {
    let arr = [];
    for (let i = 1; i < data.length - 1; i++) {
      arr.push({
        empid: data[i][0] || "NA",
        empname: data[i][1] || "NA",
        empdesignation: data[i][2] || "NA",
        empgrade: data[i][3] || "NA",
        empdivision: data[i][4] || "NA",
        emplinemanagerid: data[i][5] || "NA",
        emplinemanagername: data[i][6] || "NA",
        empemail: data[i][7] || "NA",
      });
    }
    return arr;
  }

  async function handleSubmit() {
    if (!Data || Data.length === 0) return;

    setLoading(true);
    setLoadingBtn(true);
    setSubmitted(false);

    const chunkSize = 50;
    let NEW = 0;
    let UPDATED = 0;
    let failedEmailList = [];

    for (let i = 0; i < Data.length; i += chunkSize) {
      const chunk = Data.slice(i, i + chunkSize);
      const res = await axios
        .post(`${process.env.REACT_APP_BACKEND_URL}/api/employees/bulkRewriteEmployeesv3`, {
          empArray: chunk,
          token: localStorage.getItem("token"),
        })
        .then((r) => r.data)
        .catch(() => null);

      if (res) {
        NEW += res.new || 0;
        UPDATED += res.update || 0;
        if (res.emailFailures?.length) {
          failedEmailList.push(...res.emailFailures.map((e) => e.email));
        }
      }
    }

    setFailedEmails(failedEmailList);

    setMessageText(
      `Employees Added : ${NEW}
Employees Updated : ${UPDATED}
Emails Sent: ${Data.length - failedEmailList.length}
Emails Failed: ${failedEmailList.length}`
    );

    setSubmitted(true);
    setLoading(false);
    setLoadingBtn(false);

    toast.success(`Uploaded! ${NEW} added, ${UPDATED} updated`);
  }

  async function resendFailedEmails() {
    if (failedEmails.length === 0) {
      toast.info("No failed emails to resend.");
      return;
    }

    try {
      setResendLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/employees/resendRemainingUserEmails`,
        {
          emails: failedEmails,
          token: localStorage.getItem("token"),
        }
      );

      const newFailed = res.data.failedEmails || [];
      setFailedEmails(newFailed);

      // update messageText dynamically
      const lines = MessageText?.split("\n") || [];
      setMessageText(
        `Employees Added : ${lines[0]?.split(":")[1]?.trim() || 0}
Employees Updated : ${lines[1]?.split(":")[1]?.trim() || 0}
Emails Sent: ${Data.length - newFailed.length}
Emails Failed: ${newFailed.length}`
      );

      toast.success("Resend attempt finished");
    } catch {
      toast.error("Error resending emails");
    } finally {
      setResendLoading(false);
    }
  }

  return (
    <div>
      <Button variant="contained" onClick={handleOpen}>
        Upload File
      </Button>

      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <CSVReader
            accept=".csv"
            onUploadAccepted={(res) => {
              setData(manipulateData(res.data));
              setMessageText("File uploaded. Ready to submit!");
              setSubmitted(false);
              setFailedEmails([]);
            }}
          >
            {({ getRootProps, acceptedFile, getRemoveFileProps }) => (
              <>
                <div>{acceptedFile?.name || "No file"}</div>

                <Button {...getRootProps()}>Browse</Button>
                {acceptedFile && <Button {...getRemoveFileProps()}>Remove</Button>}

                {Data && (
                  <Box sx={{ mt: 2 }}>
                    {/* Show message BEFORE submit */}
                    {!submitted && <Typography>{MessageText}</Typography>}

                    {/* Show stats AFTER submit */}
                   {submitted && (
  <>
    {/* Employee stats outside the card */}
    <Box sx={{ mb: 2 }}>
      <Typography>Employees Added: {MessageText?.split("\n")[0]?.split(":")[1]?.trim() || 0}</Typography>
      <Typography>Employees Updated: {MessageText?.split("\n")[1]?.split(":")[1]?.trim() || 0}</Typography>
      <Typography>Total Employees: {Data.length}</Typography>
    </Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Email Summary
      </Typography>

    {/* Email Summary Card */}
    <Box
      sx={{
        border: "1px solid #ddd",
        borderRadius: 2,
        p: 2,
        backgroundColor: "#f9f9f9",
      }}
    >
      <Typography sx={{ mb: 1 }}>
        Emails Sent: {Data.length - failedEmails.length}
      </Typography>
      <Typography sx={{ mb: 1 }}>
        Emails Failed: {failedEmails.length}
      </Typography>

      {/* Failed Emails List */}
      {failedEmails.length > 0 && (
        <>
          <Typography sx={{ mt: 2, mb: 1, fontWeight: "bold" }}>
            Failed Emails:
          </Typography>
          <Box
            sx={{
              maxHeight: 150,
              overflowY: "auto",
              border: "1px solid #ccc",
              borderRadius: 1,
              p: 1,
              backgroundColor: "#fff",
            }}
          >
            <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
              {failedEmails.map((email, idx) => (
                <li key={idx}>{email}</li>
              ))}
            </ul>
          </Box>
        </>
      )}
    </Box>
  </>
)}

                    {/* Submit Button */}
                    {!submitted && (
                      <LoadingButton loading={LoadingBtn} variant="contained" onClick={handleSubmit}>
                        Submit
                      </LoadingButton>
                    )}

                    {/* Resend Button */}
                    {submitted && failedEmails.length > 0 && (
                      <LoadingButton loading={resendLoading} onClick={resendFailedEmails} sx={{ mt: 1 }}>
                        Resend Emails
                      </LoadingButton>
                    )}

                    <Button
                        onClick={() => {
                          handleClose();         
                          setData(null);         
                          setMessageText(null);  
                          setFailedEmails([]);   
                          setSubmitted(false);    
                          setLoadingBtn(false);   
                        }}
                        sx={{ ml: 2 }}
                      >
                        Close
                      </Button>
                  </Box>
                )}
              </>
            )}
          </CSVReader>
        </Box>
      </Modal>
    </div>
  );
};

export default UploadFile;